import type { PeerMetadata } from '@playcastdotio/Messaging';

type CodecCapability = {
  mimeType: string;
  clockRate: number;
  channels?: number;
  sdpFmtpLine?: string;
};

export type WebRtcOptimizationConfig = {
  codecPreferences?: { video?: string[]; audio?: string[] };
  maxBitrate?: number;
  audioMaxBitrate?: number;
  targetFrameRate?: number;
  degradationPreference?: 'maintain-framerate' | 'maintain-resolution' | 'balanced';
  lowLatencyMode?: boolean;
  jitterBufferTarget?: number;
};

/**
 * Derives a WebRtcOptimizationConfig from peer metadata.
 *
 * Both desktop and mobile peers get 10 Mbit max bitrate (adjustable via
 * setBitrateOverride). Firefox peers get VP8-first codec ordering because
 * Firefox H264 support is inconsistent.
 */
export function configFromMetadata(metadata: PeerMetadata): WebRtcOptimizationConfig {
  const isFirefox = metadata.isFirefox ?? false;

  const videoCodecOrder = isFirefox
    ? ['vp8', 'h264', 'vp9']
    : ['h264', 'vp8', 'vp9'];

  return {
    codecPreferences: { video: videoCodecOrder, audio: ['opus'] },
    maxBitrate: 10_000_000,
    audioMaxBitrate: 128_000,
    targetFrameRate: 60,
    degradationPreference: 'maintain-framerate',
    lowLatencyMode: true,
    jitterBufferTarget: 0,
  };
}

/**
 * WebRtcOptimizationManager - Applies modern WebRTC optimizations.
 *
 * Uses transceiver.setCodecPreferences() for codec ordering,
 * sender.setParameters() for bitrate/fps/degradation control, and
 * SDP-level x-google-* bitrate hints (via sdpTransform on the peer
 * connection) to prevent blurry encoder ramp-up.
 *
 * Receiver jitterBufferTarget and playoutDelayHint are set for
 * lowest possible latency.
 *
 * All methods gracefully no-op when the RTCPeerConnection is null
 * (e.g. in test environments using MockPeerConnectionFactory).
 */
export class WebRtcOptimizationManager {

  applyPreConnectionOptimizations(
    rtcPc: RTCPeerConnection | null,
    config: WebRtcOptimizationConfig,
  ): void {
    if (!rtcPc) return;

    try {
      this.applyCodecPreferences(rtcPc, config);
    } catch (error) {
      console.warn('[WebRtcOptimizationManager] Pre-connection optimization failed:', error);
    }
  }

  applyPostConnectionOptimizations(
    rtcPc: RTCPeerConnection | null,
    config: WebRtcOptimizationConfig,
  ): void {
    if (!rtcPc) return;

    try {
      this.applySenderParameters(rtcPc, config);
      if (config.lowLatencyMode !== false) {
        this.applyReceiverLowLatencySettings(rtcPc, config);
      }
    } catch (error) {
      console.warn('[WebRtcOptimizationManager] Post-connection optimization failed:', error);
    }
  }

  private applyCodecPreferences(
    rtcPc: RTCPeerConnection,
    config: WebRtcOptimizationConfig,
  ): void {
    const transceivers = rtcPc.getTransceivers();

    for (const transceiver of transceivers) {
      const kind = this.resolveTransceiverKind(transceiver);
      if (!kind) continue;

      try {
        if (kind === 'video' && config.codecPreferences?.video) {
          this.setCodecPreferencesForKind(transceiver, 'video', config.codecPreferences.video, config);
        } else if (kind === 'audio' && config.codecPreferences?.audio) {
          this.setCodecPreferencesForKind(transceiver, 'audio', config.codecPreferences.audio, config);
        }
      } catch (error) {
        console.warn(`[WebRtcOptimizationManager] Failed to set ${kind} codec preferences:`, error);
      }
    }
  }

  private resolveTransceiverKind(
    transceiver: RTCRtpTransceiver,
  ): 'audio' | 'video' | null {
    const kind =
      transceiver.sender?.track?.kind ??
      transceiver.receiver?.track?.kind ??
      null;

    if (kind === 'audio' || kind === 'video') return kind;
    return null;
  }

  private setCodecPreferencesForKind(
    transceiver: RTCRtpTransceiver,
    kind: 'video' | 'audio',
    preferredOrder: string[],
    config: WebRtcOptimizationConfig,
  ): void {
    const hasSenderTrack = !!transceiver.sender?.track;
    const capabilities = hasSenderTrack
      ? RTCRtpSender.getCapabilities(kind)
      : RTCRtpReceiver.getCapabilities(kind);
    if (!capabilities?.codecs) return;

    type Codec = (typeof capabilities.codecs)[number];
    const ordered: Codec[] = [];

    for (const preferred of preferredOrder) {
      const matching = capabilities.codecs.filter(
        (c) => c.mimeType.toLowerCase().includes(preferred.toLowerCase()),
      );
      ordered.push(...matching);
    }

    const remaining = capabilities.codecs.filter(
      (c) => !ordered.includes(c),
    );
    ordered.push(...remaining);

    if (ordered.length === 0) return;

    // For audio, try applying Opus stereo/bitrate fmtp. Chrome validates codec
    // objects against getCapabilities() and rejects modified sdpFmtpLine values.
    // When that happens, fall back to unmodified ordering — codec priority is
    // preserved and audio bitrate is still applied post-connection via
    // sender.setParameters().
    if (kind === 'audio') {
      const withFmtp = this.applyOpusStereoFmtp(ordered, config);
      try {
        transceiver.setCodecPreferences(withFmtp);
        return;
      } catch {
        // Modified codecs rejected; fall through to unmodified ordering
      }
    }

    transceiver.setCodecPreferences(ordered);
  }

  private applyOpusStereoFmtp(codecs: CodecCapability[], config: WebRtcOptimizationConfig): CodecCapability[] {
    const bitrate = config.audioMaxBitrate;
    return codecs.map((codec) => {
      if (!codec.mimeType.toLowerCase().includes('opus')) return codec;

      const existingFmtp = codec.sdpFmtpLine ?? '';
      const params = new Map<string, string>();
      if (existingFmtp) {
        for (const part of existingFmtp.split(';')) {
          const [key, val] = part.split('=');
          if (key) params.set(key.trim(), val?.trim() ?? '');
        }
      }

      params.set('stereo', '1');
      params.set('sprop-stereo', '1');
      if (bitrate && Number.isFinite(bitrate) && bitrate > 0) {
        params.set('maxaveragebitrate', String(bitrate));
      }

      const fmtpLine = Array.from(params.entries())
        .map(([k, v]) => `${k}=${v}`)
        .join(';');

      return { ...codec, sdpFmtpLine: fmtpLine };
    });
  }

  applySenderParameters(
    rtcPc: RTCPeerConnection,
    config: WebRtcOptimizationConfig,
  ): void {
    const senders = rtcPc.getSenders();

    for (const sender of senders) {
      if (!sender.track) continue;

      if (sender.track.kind === 'video') {
        this.applyVideoSenderParams(sender, config);
      } else if (sender.track.kind === 'audio') {
        this.applyAudioSenderParams(sender, config);
      }
    }
  }

  private applyVideoSenderParams(sender: RTCRtpSender, config: WebRtcOptimizationConfig): void {
    const params = sender.getParameters() as RTCRtpSendParameters;
    if (!params.encodings || params.encodings.length === 0) return;

    const encoding = params.encodings[0];

    if (config.maxBitrate && Number.isFinite(config.maxBitrate) && config.maxBitrate > 0) {
      encoding.maxBitrate = config.maxBitrate;
    }

    const fps = config.targetFrameRate;
    if (fps && Number.isFinite(fps) && fps > 0) {
      encoding.maxFramerate = fps;
    }

    encoding.priority = 'high';
    (encoding as any).networkPriority = 'high';

    if (config.degradationPreference) {
      params.degradationPreference = config.degradationPreference;
    }

    sender.setParameters(params).catch((err) => {
      console.warn('[WebRtcOptimizationManager] Failed to set video sender parameters:', err);
    });
  }

  private applyAudioSenderParams(sender: RTCRtpSender, config: WebRtcOptimizationConfig): void {
    const bitrate = config.audioMaxBitrate;
    if (!bitrate || !Number.isFinite(bitrate) || bitrate <= 0) return;

    const params = sender.getParameters() as RTCRtpSendParameters;
    if (!params.encodings || params.encodings.length === 0) return;

    params.encodings[0].maxBitrate = bitrate;

    sender.setParameters(params).catch((err) => {
      console.warn('[WebRtcOptimizationManager] Failed to set audio sender parameters:', err);
    });
  }

  private applyReceiverLowLatencySettings(
    rtcPc: RTCPeerConnection,
    config: WebRtcOptimizationConfig,
  ): void {
    const target = config.jitterBufferTarget ?? 0;
    const receivers = rtcPc.getReceivers();

    for (const receiver of receivers) {
      if (!receiver.track) continue;

      if ('jitterBufferTarget' in receiver) {
        (receiver as any).jitterBufferTarget = target;
      }

      if ('playoutDelayHint' in receiver) {
        (receiver as any).playoutDelayHint = target;
      }
    }
  }
}
